const hasOwn = Object.prototype.hasOwnProperty;

function hasObjectPrototype(value: unknown): boolean {
	return Object.prototype.toString.call(value) === "[object Object]";
}

function isPlainObject(value: unknown): value is Record<PropertyKey, unknown> {
	if (!hasObjectPrototype(value)) {
		return false;
	}

	const ctor = (value as { constructor?: unknown }).constructor;
	if (ctor === undefined) {
		return true;
	}

	const prot = (ctor as { prototype?: unknown }).prototype;
	if (!hasObjectPrototype(prot)) {
		return false;
	}

	if (!Object.hasOwn(prot as object, "isPrototypeOf")) {
		return false;
	}

	return Object.getPrototypeOf(value) === Object.prototype;
}

function isPlainArray(value: unknown): value is unknown[] {
	return Array.isArray(value) && value.length === Object.keys(value).length;
}

function isDateInstance(value: unknown): value is Date {
	return value instanceof Date;
}

function replaceEqualDeep(a: unknown, b: unknown, depth = 0): unknown {
	if (a === b) {
		return a;
	}

	if (depth > 500) {
		return b;
	}

	if (isDateInstance(a) && isDateInstance(b)) {
		const aTime = a.getTime();
		const bTime = b.getTime();
		// NaN pair = equal invalid Dates; without this, NaN !== NaN forces a new
		// reference for them and every ancestor on every refetch, permanently.
		if (aTime === bTime || (Number.isNaN(aTime) && Number.isNaN(bTime))) {
			return a;
		}
	}

	const array = isPlainArray(a) && isPlainArray(b);
	if (!(array || (isPlainObject(a) && isPlainObject(b)))) {
		return b;
	}

	const aRecord = a as Record<PropertyKey, unknown>;
	const bRecord = b as Record<PropertyKey, unknown>;
	const aSize = array ? (a as unknown[]).length : Object.keys(aRecord).length;
	const bKeys = array ? [] : Object.keys(bRecord);
	const bSize = array ? (b as unknown[]).length : bKeys.length;
	const copy = (array ? new Array(bSize) : {}) as Record<PropertyKey, unknown>;

	let equalItems = 0;
	for (let i = 0; i < bSize; i++) {
		const key: PropertyKey = array ? i : (bKeys[i] as string);
		const aItem = aRecord[key];
		const bItem = bRecord[key];

		if (aItem === bItem) {
			copy[key] = aItem;
			if (array ? i < aSize : hasOwn.call(aRecord, key)) {
				equalItems++;
			}
			continue;
		}

		if (
			aItem === null ||
			bItem === null ||
			typeof aItem !== "object" ||
			typeof bItem !== "object"
		) {
			copy[key] = bItem;
			continue;
		}

		const replaced = replaceEqualDeep(aItem, bItem, depth + 1);
		copy[key] = replaced;
		if (replaced === aItem) {
			equalItems++;
		}
	}

	return aSize === bSize && equalItems === aSize ? a : copy;
}

/**
 * Vendored query-core `replaceEqualDeep`
 * (github.com/TanStack/query/blob/v5.101.0/packages/query-core/src/utils.ts)
 * plus one branch: time-equal Dates are equal, so superjson-revived Prisma
 * timestamps keep referential identity on refetch. The parity suite pins the
 * copy; if a bump breaks it, pre-align Dates and delegate to upstream instead.
 */
export function dateAwareStructuralSharing(
	oldData: unknown,
	newData: unknown,
): unknown {
	return replaceEqualDeep(oldData, newData);
}
